<template>
  <register-layout>
    <!-- start login -->
    <div class="layer-form">
      <logo @click="gostore" class="logo"></logo>
      <h1>{{ t('login.sign') }}</h1>

      <a-form :layout="'vertical'" :model="loginState" :rules="rules" ref="loginForm" class="customer-form" @keyup.enter.native="loginFn">

        <a-form-item name="email" :label="t('placeholder.emailOrJackeryID')" :class="loginState.email || state.emailFocus ? 'focused-input' : ''">
          <a-input class="just-bottom" autofocus="autofocus" size="large" v-model:value="loginState.email" @focus="focusFn('emailFocus')"
                   @blur="blurFn('emailFocus')"  @input="debounceCheckEmail(loginState.email)" :placeholder="t('placeholder.emailOrJackeryID')" :allowClear="true" autoComplete="off">
          </a-input>
        </a-form-item>

        <a-form-item name="password" :label="t('placeholder.password')" :class="loginState.password || state.pswFocus ? 'focused-input' : ''">
          <a-input class="just-bottom" size="large" :maxlength="16" v-model:value="loginState.password" @focus="focusFn('pswFocus')"
                   @blur="blurFn('pswFocus')" :placeholder="t('placeholder.password')" :allowClear="true" type="password" autoComplete="off">
            <template #suffix>
              <router-link class="a-btn" to="/reset">{{ t('login.forgot') }}</router-link>
            </template>
          </a-input>
        </a-form-item>

         <a-form-item name="agree" class="no-bottom"  v-if="state.showAgree" >
          <label class="y-checkbox">
            <input type="checkbox" v-model="loginState.agree" />
            <span class="text" v-if="language_current.cr === 'JP'">
              <a :href="terms" target='_blank'>{{ t('policies.terms') }}</a>
              &nbsp;{{ t('policies.and') }}&nbsp;
              <a :href="policy" target='_blank'>{{ t('policies.policy') }}</a>
              &nbsp;{{ t('register.agree') }}
            </span>
            <span class="text" v-else>
              {{ t('register.agree') }}&nbsp;
              <a :href="policy" target='_blank'>{{ t('policies.policy') }}</a>
              &nbsp;{{ t('policies.and') }}&nbsp;
              <a :href="terms" target='_blank'>{{ t('policies.terms') }}</a>
              {{ t('register.agree_after') }}
            </span>
          </label>
        </a-form-item>
      </a-form>


      <a-spin :spinning="state.subLoading">
        <a-button type="primary" size="large" class="login-btn" :disabled="state.submit" @click="loginFn">{{ t('login.sign') }}</a-button>
      </a-spin>

      <router-link class="go-register-btn a-btn" :to="state.registerUrl" >{{ t('login.create') }}</router-link>
      <div class="go-quick-btn go-register-btn a-btn"  @click="toQuickLogin">{{ t('login.quickLoginA') }}</div>
      <third-login></third-login>
      <account-message></account-message>
    </div>
    <!-- end login -->

    <!-- account activated -->
    <a-modal v-model:visible="activated.visible" :title="t('login.accountActivate')" wrap-class-name="mobile-full-modal"
             :okText="t('operate.submit')" :cancelText="t('operate.cancel')" :centered="true" @ok="handleOk"
             :confirm-loading="activated.loading" :ok-button-props="{ disabled: activated.code.length < 4 }">
      <template #closeIcon>
        <jds-icon name="close-fill" class="text-#999 hover:text-#666 transition-all duration-300 text-48 lg:a-text-32"></jds-icon>
      </template>
      <a-input-group compact>
        <a-input size="large" v-model:value="activated.code" :placeholder="t('tips.verify')"></a-input>
        <loading-outlined class="verify" v-if="activated.timerLoading" />
        <i v-else :class="`verify ${activated.timer ? 'off' : 'on'}`" @click.prevent="getVerifyCode">{{ activated.timer ? `${activated.timer}s` : t('tips.getVerifyCode') }}</i>
      </a-input-group>
    </a-modal>

    <!-- third login account bind email -->
    <a-modal v-model:visible="bindActivated.visible" :title="t('login.bindEmail')" wrap-class-name="mobile-full-modal"
             :okText="t('operate.submit')" :cancelText="t('operate.cancel')" :centered="true" @ok="handleBindEmail"
             :confirm-loading="activated.loading" :ok-button-props="{ disabled: activated.code.length < 4 }">
      <template #closeIcon>
        <jds-icon name="close-fill" class="text-#999 hover:text-#666 transition-all duration-300 text-48 lg:a-text-32"></jds-icon>
      </template>
      <a-input size="large" v-model:value="bindActivated.email" :placeholder="t('account.email')"></a-input>
      <br /><br />
      <a-input-group compact>
        <a-input size="large" v-model:value="activated.code" :placeholder="t('tips.verify')"></a-input>
        <loading-outlined class="verify" v-if="activated.timerLoading" />
        <i v-else :class="`verify ${activated.timer || !verifyEmail(bindActivated.email) ? 'off' : 'on'}`" @click.prevent="getVerifyCode('channelBindEmail')">{{ activated.timer ? `${activated.timer}s` : t('tips.getVerifyCode') }}</i>
      </a-input-group>
    </a-modal>
  </register-layout>
</template>

<script setup>
  import { COUNTRY_TERMS, COUNTRY_POLICY } from "@u/helpers/siteData";
  import { LoadingOutlined } from '@ant-design/icons-vue';
  import RegisterLayout from "@c/RegisterLayout.vue";
  import Logo from "@c/Logo.vue";
  import ThirdLogin from "@c/ThirdLogin.vue";
  import AccountMessage from "@c/AccountMessage.vue";
  import { verifyPassWdCompose, verifyEmail, deepClone, countdown, getUrlParam, formValidate, } from "@u/common";
  import { getEmailCode, verifyCodeForEmail, passwordModify, ThirdLoginBindEmail } from "@a/account";
  import { checkEmail, agreeEmail } from '@a/index';
  import { message } from "ant-design-vue";
  import { useStore } from 'vuex';

  const { t } = useI18n();
  const route = useRoute();
  const store = useStore();
  const language_current = computed(() => store.getters.language.current);
  const policy = computed(() => COUNTRY_POLICY.get(store.getters.language.current.cr));
  const terms = computed(() => COUNTRY_TERMS.get(store.getters.language.current.cr));

  const loginForm = ref(null);
  const loginState = reactive({ email: "", password: "", agree: false });
  const state = reactive({ 
    emailFocus: false,
    pswFocus: false,
    subLoading: false,
    submit: true,
    showAgree: false,   // ⚡ 新增：控制是否显示协议勾选框
    registerUrl: {
    path: '/register',
    query: {}
    }
  });

  // account
  const activated = reactive({
    visible: false, loading: false, code: '', timer: 0, timerLoading: false, timerInterval: null
  });

  // third login account bind email
  const bindActivated = reactive({ visible: false, email: "" });

  const focusFn = (inputName) => { state[inputName] = true };
  const blurFn = (inputName) => { 
    state[inputName] = false;

    // ⚡ 如果是 email 输入框失焦，调用接口
    if (inputName === 'emailFocus' && loginState.email) {
      checkEmailPolicy(loginState.email);
    }
  };


  const checkEmailPolicy = async (email) => {
    if (!verifyEmail(email)) {
      state.showAgree = false;  // 邮箱不合法直接不显示
      return;
    }

    try {

      let cr = language_current.value.cr
      const res = await checkEmail({ email,cr });
      state.showAgree = !res;
    } catch (e) {
      state.showAgree = false;
    }
  };

    // 防抖工具函数 (JS 版本)
  const debounce = (func, delay = 600) => {
    let timeoutId = null
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = setTimeout(() => {
        func.apply(null, args)
      }, delay)
    }
  }


  const debounceCheckEmail = debounce((email) => {
    checkEmailPolicy(email)
  }, 500)


  const gostore = () => {
    window.location.href = language_current.value.domain
  }
  const toQuickLogin = () =>{
      // 是否为 iOS 移动终端
      const isiOS = () => !!navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
      // 因为苹果的安全机制拦截了，所以不能打开新的标签页
      let link =`${language_current.value.domain}/pages/login?return_url=${location.origin}/account/overview`
      isiOS() ? (window.location.href = link) : window.open(link)
  }

  // 校验密码
  const validatePass = async (_rule, value) => {
    return verifyPassWdCompose(value.trim()) ? Promise.reject(t('login.illegal_password')) : Promise.resolve()
  };

  // 检验邮箱或jackery id
  const validateMailOrJid = async (_rule, value) => {
    if (value.trim().indexOf("@") >= 0) {
      return verifyEmail(value.trim()) ? Promise.resolve() : Promise.reject(t('login.illegal_email'))
    } else {
      return value.trim().length >= 6 ? Promise.resolve() : Promise.reject(t('login.enterEmail'))
    }
  }

  // 表单校验规则
  const rules = {
    email: [
      {required: true, validator: validateMailOrJid, trigger: ["blur", "change"]}
    ],
    password: [
      {required: true, validator: validatePass, trigger: ["blur", "change"]}
    ],
    agree: [
      {
        required: state.showAgree,
        validator: (_rule, value) => {
          return value ? Promise.resolve() : Promise.reject(t('register.agree_first'))
        },
        trigger: ["change"]
      }
    ]
  };

  const handleOtherParams = () => {
    let { uparams} = route.query;
    // 注册请求附加参数传参处理： params、act_params、register_act、tag等
    // JSON.parse -- decodeURIComponent
    let fetchParams = {}
    try {
      if(uparams) {
        const _uparams = JSON.parse(decodeURIComponent(uparams))
        if(typeof _uparams === 'object') {
          fetchParams = {..._uparams}
        }
      }
    } catch (error) {
      console.error(error)
    }
    return fetchParams
  }

  /**
   * shopify user first need activated, code is -519
   * if referrer origin is shopify, need use interface 'shopifyLogin' api, then back shopify website
   * if exist checkout_url, first jump to shopify checkout
   * activate must jump to shopify snyc api
   */
  const loginFn = (activate = false) => {
    let jackeryRef = sessionStorage.getItem('JACKERY_ID_REF'),
        registerAppId = getUrlParam("registerAppId"),
        return_url = getUrlParam("return_url"),
        redirect = getUrlParam("redirect"),
        checkout_url = getUrlParam("checkout_url"),
        support_url = getUrlParam("s_url"),
        support_shop_id = getUrlParam("s_shop_id"),
        shopify_shop_id = getUrlParam("shopify_shop_id"),
        ty_token = getUrlParam("ty_token");

    loginForm.value.validate().then(() => {
      state.subLoading = true;
      loginState.email = loginState.email.trim();

      let params = deepClone(loginState)
      registerAppId && (params.registerAppId = decodeURIComponent(registerAppId));

      params.activate = activate
      const queryParams = {...handleOtherParams()}

      if(queryParams.return_url) {
        params.return_url = queryParams.return_url
      }

      store.dispatch('app/loginByPassword', {
        ...params, 
        uparams: {
          ...queryParams, 
          email: params.email, 
          subscribe: params.subscribe
        }
      })
      .then(()=>{
        try {
          let cr = language_current.value.cr
          let email = loginState.email
          const res = agreeEmail({ email, cr });
        } catch (e) {
        }
      })
      .catch((e) => {
        state.subLoading = false;

        // FIX: USERLOGIN CODE -519
        e.code && e.code === -519 && (activated.visible = true);
      })
    })
  };

  const getVerifyCode = (type) => {
    let api, param = { email: "", type: 3 };
    if (type === "channelBindEmail") {
      api = ThirdLoginBindEmail;
      param.email = bindActivated.email
    } else {
      api = getEmailCode;
      param.email = loginState.email
    }
    api(param).then(() => {
      message.success({ content: t('tips.sendVerify'), duration: 5 });
      activated.timerLoading = false;
      countdown(activated, false)
    }).catch(() => {
      activated.timerLoading = false
    })
  }

  // shopify用户激活流程: 自动验证和修改密码, 最后自动登录
  const handleOk = () => {
    verifyCodeForEmail({ 
      email: loginState.email, 
      code: activated.code 
    }).then(() => {
      passwordModify({ 
        ...loginState, 
        verificationCode: activated.code, 
        verificationCodeType: 3 
      }).then(() => {
        activated.loading = activated.visible = false;
        loginFn(true)
      }).catch(() => {
        activated.loading = false
      })
    }).catch(() => {
      message.error(t('tips.verifyError'), 5);
      activated.loading = false
    })
  }

  const handleBindEmail = async () => {
    const { uid, source, belongBrandChannel } = route.query;
    store.dispatch('app/thirdLogin', {
        emailExist: 0, 
        "email": bindActivated.email,
        "verificationCode": activated.code,
        belongBrandChannel, 
        source, 
        uid
    })
  }

  const handelThirdBindEmail = () => {
    const {source, emailExist, uid, belongBrandChannel} = route.query
    if(uid && emailExist === '0') {
      bindActivated.visible = true;
      countdown(activated, true)
    }
  }

  // 表单循环校验, 而不是所有的一起校验, 抛弃loginForm.value.validate()
  watch(loginState, (v) => formValidate(rules, v, state, "submit"));
  watch(language_current, (v, o) => { // 切换语言 - 重置表单
    loginForm.value.resetFields()
  });
  watch(() => state.showAgree, (val) => {
    rules.agree[0].required = val;
     // ⚡ 强制刷新校验
    formValidate(rules, loginState, state, "submit");
  });

  onBeforeMount(() => {
    // 邮箱自动填充
    let { email } = route.query;
    email && (loginState.email = email);
  });

  // 自动登录验证
  onMounted(() => {
    handleOtherParams()
    // 解决浏览器自动填充问题
    if (document.querySelector("#form_item_email")) {
      setTimeout(() => {
        let input = document.querySelector("#form_item_email");
        let evt = new CustomEvent("input");
        input.dispatchEvent(evt)
      }, 1000)
    }
    
    // 跳转register的时候，把参数带过去。
    state.registerUrl.query = route.query;

    // FIX: 三方登录未绑定邮箱
    handelThirdBindEmail()
  })
</script>

<style scoped lang="less">
  .customer-form {margin-bottom: var(--gap-mini);}
  .a-btn {font-size: var(--font-normal); color: var(--blue-button); display: inline-block;}
  .login-btn {width: 100%; text-transform: uppercase;}
  .go-register-btn {margin: var(--gap) 0;}
  .JP .go-register-btn {
    width: 100%; height: 48px; line-height: 48px; text-align: center; color: var(--regular-color);
    border: var(--line-color2); border-radius: 4px;
  }

  .go-quick-btn{
    display: block;
    margin-top: 0;
    cursor: pointer;
  }

  .text {color: var(--regular-color);}
  .text a {text-decoration: underline; color: var(--regular-color);}

  .y-checkbox {
    display: inline-block;
    span {font-size: var(--font-small); padding-left: 0; text-indent: 2em;}
    span::before {top: 5px;}
    span::after {top: 8px;}
  }

  .ant-form-item.no-bottom{
    margin-top: -20px;
    margin-bottom: 16px;
  }

  @media screen and (max-width: 1280px) {
    .go-register-btn {margin: 10px 0;}

    .go-quick-btn{
      margin-top: 0;
    }

    .y-checkbox {
      span {line-height: 16px;}
      span::before {top: 0;}
      span::after {top: 3px;}
    }
  }

  @media (max-width: 540px) {
    @w: 100vw/750;
    .go-register-btn {text-align: center;}

      .ant-form-item.no-bottom{
        margin-top: (17 * @w);
        margin-bottom: (37 * @w);
    }
  }
</style>
